import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { BiDownload } from "react-icons/bi";
import { FiEdit2 } from "react-icons/fi";

import DeleteButton from "../../../../components/DeleteButton/DeleteButton";
import Button from "../../../../components/Utils/Button/Button";
import DynamicTable from "../../../../components/Utils/DynamicTable/DynamicTable";
import Spinner from "../../../../components/Utils/Spinner/Spinner";
import useLoadingStore from "../../../../stores/LoadingStore";
import useUserStore from "../../../../stores/UserStore";
import AccountingExpenseType from "../../../../types/AccountingExpense";
import { formatCurrency } from "../../../../Utils/formatCurrency";
import { formatDateParam } from "../../../../Utils/formatDateParam";
import appFetch from "../../../../Utils/Services/Fetch/appFetch";

// Effective VAT in currency: the directly recorded amount when present,
// otherwise derived from the rate (price * rate / 100).
const getExpenseTvaAmount = (item: AccountingExpenseType): number => {
  if (item.tva_amount !== null && item.tva_amount !== undefined) {
    return Number(item.tva_amount);
  }
  return ((Number(item.price) || 0) * (Number(item.tva_rate) || 0)) / 100;
};

const ExpenseList: React.FC<{
  onEdit: (expense: AccountingExpenseType) => void;
  onEditPage?: boolean;
  search: string;
  startDate: Date;
  endDate: Date;
}> = ({ onEdit, onEditPage, search, startDate, endDate }) => {
  const [expense, setExpense] = useState<AccountingExpenseType[]>();
  const { loadingStates, setLoading } = useLoadingStore();
  const { isVatSubject } = useUserStore();
  const [sortState, setSortState] = useState<{
    column: string;
    direction: "asc" | "desc";
  } | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading("getExpenses", true);
      const expensesData = await appFetch("/api/accounting/getExpenses", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          startDate: formatDateParam(startDate),
          endDate: formatDateParam(endDate),
        }),
      });
      if (expensesData) {
        setExpense(expensesData.expenses);
      }
      setLoading("getExpenses", false);
    };

    fetchData();
  }, [endDate, startDate, setLoading]);

  const handleSort = (column: keyof AccountingExpenseType | "event.name") => {
    const direction = sortState?.direction === "asc" ? "desc" : "asc";
    setSortState({ column, direction });
    setExpense((prevExpenses) => {
      if (!prevExpenses) return prevExpenses;
      return prevExpenses.slice().sort((a, b) => {
        let aValue: any;
        let bValue: any;
        if (column === "event.name") {
          aValue = a.event?.name;
          bValue = b.event?.name;
        } else if (column === "price" || column === "tva_rate") {
          aValue = Number(a[column] ?? 0);
          bValue = Number(b[column] ?? 0);
        } else if (column === "tva_amount") {
          aValue = getExpenseTvaAmount(a);
          bValue = getExpenseTvaAmount(b);
        } else {
          aValue = a[column];
          bValue = b[column];
        }
        if (aValue == null || bValue == null) return 0;
        if (aValue < bValue) return direction === "asc" ? -1 : 1;
        if (aValue > bValue) return direction === "asc" ? 1 : -1;
        return 0;
      });
    });
  };

  const filteredExpenses = expense?.filter((item) => {
    return (
      item.product_name.toLowerCase().includes(search.toLowerCase()) ||
      item.user_name?.toLowerCase().includes(search.toLowerCase()) ||
      item.event?.name?.toLowerCase().includes(search.toLowerCase())
    );
  });

  const downloadExpense = async (item: AccountingExpenseType) => {
    setLoading(`downloadExpense${item.expense_id}`, true);
    try {
      const fileUrl = `/api/accounting/getExpensesFile?expense_id=${item.expense_id}`;
      const link = document.createElement("a");
      link.href = fileUrl;
      link.download = item.product_name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      toast.error("Erreur lors du téléchargement du fichier");
    }
    setLoading(`downloadExpense${item.expense_id}`, false);
  };

  const deleteExpense = async (expenseId: number) => {
    setLoading(`deleteExpense${expenseId}`, true);
    const response = await appFetch(
      `/api/accounting/deleteExpense?expense_id=${expenseId}`,
      {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ expense_id: expenseId }),
      },
    );

    if (response && response.error) {
      toast.error(response.message);
    } else if (response && response.message) {
      toast.success(response.message);

      setExpense((prevExpenses) => {
        return prevExpenses?.filter((item) => item.expense_id !== expenseId);
      });

      document.getElementById("cancelModalButton")?.click();
    }
    setLoading(`deleteExpense${expenseId}`, false);
  };

  let columns = [
    {
      title: "Nom",
      dataIndex: "product_name",
      sortable: true,
      onSort: () => handleSort("product_name"),
      sortableClass:
        sortState?.column === "product_name" ? sortState.direction : undefined,
    },
    {
      title: "Acheteur",
      dataIndex: "user_name",
      render: (_user_name: string, item: AccountingExpenseType) => {
        return item.user_name ? item.user_name : "Organisation";
      },
      sortable: true,
      onSort: () => handleSort("user_name"),
      sortableClass:
        sortState?.column === "user_name" ? sortState.direction : undefined,
    },
    {
      title: "Date",
      dataIndex: "recorded_date",
      render: (value: string) => {
        return new Date(value).toLocaleDateString("fr-FR", {
          year: "numeric",
          month: "numeric",
          day: "numeric",
        });
      },
      sortable: true,
      onSort: () => handleSort("recorded_date"),
      sortableClass:
        sortState?.column === "recorded_date" ? sortState.direction : undefined,
    },
    {
      title: "Mode de paiement",
      dataIndex: "payment_method",
      sortable: true,
      onSort: () => handleSort("payment_method"),
      sortableClass:
        sortState?.column === "payment_method"
          ? sortState.direction
          : undefined,
    },
    {
      title: "Event",
      dataIndex: "event.name",
      render: (_event: string, item: AccountingExpenseType) => {
        return item.event?.name ?? "N/A";
      },
      sortable: true,
      onSort: () => handleSort("event.name"),
      sortableClass:
        sortState?.column === "event.name" ? sortState.direction : undefined,
    },
    {
      title: "Prix",
      dataIndex: "price",
      sortable: true,
      onSort: () => handleSort("price"),
      sortableClass:
        sortState?.column === "price" ? sortState.direction : undefined,
    },
    ...(isVatSubject
      ? [
          {
            title: "TVA (%)",
            dataIndex: "tva_rate",
            render: (_value: number, item: AccountingExpenseType) => {
              if (item.tva_amount !== null && item.tva_amount !== undefined) {
                return "—";
              }
              return `${item.tva_rate ?? 0}%`;
            },
            sortable: true,
            onSort: () => handleSort("tva_rate"),
            sortableClass:
              sortState?.column === "tva_rate"
                ? sortState.direction
                : undefined,
          },
          {
            title: "Montant TVA",
            dataIndex: "tva_amount",
            render: (_value: number, item: AccountingExpenseType) => {
              return `${formatCurrency(getExpenseTvaAmount(item))} €`;
            },
            sortable: true,
            onSort: () => handleSort("tva_amount"),
            sortableClass:
              sortState?.column === "tva_amount"
                ? sortState.direction
                : undefined,
          },
        ]
      : []),
    {
      title: "Actions",
      dataIndex: "actions",
      render: (_: string, record: AccountingExpenseType) => (
        <div style={{ display: "flex", gap: "10px" }}>
          <Button
            className="downloadButton"
            onClick={() => downloadExpense(record)}
            icon={<BiDownload />}
            isLoading={loadingStates[`downloadExpense${record.expense_id}`]}
            style="square"
          />
          <Button
            className="editButton"
            onClick={() => onEdit(record)}
            icon={<FiEdit2 />}
            style="square"
          />
          <DeleteButton
            onClick={() => deleteExpense(record.expense_id)}
            isLoading={loadingStates[`deleteExpense${record.expense_id}`]}
          />
        </div>
      ),
    },
  ];

  if (onEditPage) {
    columns = columns.filter((column) => column.title !== "Actions");
  }

  const handleRowClick = (item: AccountingExpenseType) => {
    if (onEditPage) {
      onEdit(item);
    }
  };

  return (
    <div className={`accountingListPage ${onEditPage ? "onEditPage" : ""}`}>
      <div className="tableContainer">
        {loadingStates.getExpenses ? (
          <Spinner />
        ) : (
          <DynamicTable
            data={filteredExpenses ?? []}
            columns={columns}
            onClick={handleRowClick}
            pagined
            maxRow={25}
            mobile
            renderSummary={(visibleExpenses) => {
              const total = visibleExpenses.reduce(
                (sum: number, item: AccountingExpenseType) =>
                  sum + Number(item.price ?? 0),
                0,
              );

              return `Montant total: ${formatCurrency(total)} €`;
            }}
          />
        )}
      </div>
    </div>
  );
};

export default ExpenseList;
